import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { trackEvent } from "../lib/analytics";

/**
 * Desktop-only exit-intent quote popup.
 *
 *  - Listens for cursor leaving the viewport from the top edge (browser back /
 *    address-bar / close-tab intent).
 *  - Mobile: skipped entirely (no mouseleave equivalent + intrusive on small
 *    screens; sticky CTA bar covers mobile lead-cap).
 *  - **Frequency cap:** Once shown OR dismissed, suppressed for 1 hour
 *    (across page navigations + page reloads — localStorage-backed).
 *  - Long-term dismiss after explicit close: 14 days.
 *  - Suppressed on /admin, /checkout, /success, /quote (don't badger users
 *    already converting).
 *  - Fires GA4 events `exit_intent_shown` and `exit_intent_quote_click`.
 */
const KEY = "tc_exit_intent_dismissed_v1";   // explicit dismiss → 14d suppress
const SHOWN_KEY = "tc_exit_intent_shown_v1"; // just shown → 1h suppress
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
const SHOWN_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
const HIDE_PREFIXES = ["/admin", "/checkout", "/success", "/quote"];

export default function ExitIntentPopup() {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [armed, setArmed] = useState(false);

  // Don't even mount the listener on hidden routes
  const hidden = HIDE_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (hidden) return;
    if (typeof window === "undefined") return;
    // Mobile guard — only desktop has meaningful mouseleave
    if (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) return;
    try {
      // 14-day suppression after explicit dismiss
      const dismissed = localStorage.getItem(KEY);
      if (dismissed && Date.now() - Number(dismissed) < DISMISS_MS) return;
      // 1-hour cooldown after just being shown
      const shown = localStorage.getItem(SHOWN_KEY);
      if (shown && Date.now() - Number(shown) < SHOWN_COOLDOWN_MS) return;
    } catch { /* no-op */ }

    // Arm the listener only after 8 seconds of engagement (avoid showing on
    // people who instantly bounce — they were never going to convert).
    const armTimer = setTimeout(() => setArmed(true), 8000);
    return () => clearTimeout(armTimer);
  }, [hidden, pathname]);

  useEffect(() => {
    if (!armed) return;
    const onLeave = (e) => {
      // Only trigger when cursor exits via the TOP of the viewport.
      if (e.clientY <= 5 && !show) {
        setShow(true);
        try { localStorage.setItem(SHOWN_KEY, String(Date.now())); } catch { /* no-op */ }
        trackEvent("exit_intent_shown", { path: window.location.pathname });
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [armed, show]);

  const dismiss = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch { /* no-op */ }
    setShow(false);
  };

  if (hidden || !show) return null;

  return (
    <div
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4 animate-[fadeIn_200ms_ease-out]"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="surface-card max-w-lg w-full p-8 relative"
      >
        <button
          onClick={dismiss}
          data-testid="exit-intent-close"
          aria-label="Close popup"
          className="absolute top-4 right-4 text-zinc-500 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest text-[var(--tc-red)] mb-3 uppercase">
          <Sparkles size={14} /> Wait — Free Quote
        </div>
        <h2 className="font-display text-4xl md:text-5xl uppercase leading-none mb-4">
          Before You Go.
        </h2>
        <p className="font-body text-zinc-300 text-base leading-relaxed mb-6">
          Need bulk pricing for your team, school, or event? Get a free quote in
          under 24 hours — no minimums, fast Mississauga turnaround.
        </p>

        <ul className="text-sm text-zinc-300 mb-6 space-y-2 font-body">
          <li className="flex items-center gap-2">
            <span className="text-[var(--tc-red)]">✓</span> 24-hour quote response
          </li>
          <li className="flex items-center gap-2">
            <span className="text-[var(--tc-red)]">✓</span> Bulk pricing on 12+ pieces
          </li>
          <li className="flex items-center gap-2">
            <span className="text-[var(--tc-red)]">✓</span> Free local pickup or shipping
          </li>
        </ul>

        <Link
          to="/quote"
          onClick={() => {
            trackEvent("exit_intent_quote_click", { source: "exit_intent" });
            dismiss();
          }}
          data-testid="exit-intent-cta"
          className="btn-tc-primary inline-flex items-center justify-center gap-2 w-full"
        >
          Get My Free Quote <ArrowRight size={16} />
        </Link>

        <button
          onClick={dismiss}
          data-testid="exit-intent-dismiss-text"
          className="block w-full mt-3 font-mono text-[10px] tracking-widest uppercase text-zinc-500 hover:text-zinc-300 py-2"
        >
          No thanks, just browsing
        </button>
      </div>
    </div>
  );
}
